import { useRef, useEffect, useCallback } from 'react'
import { playCountdownTick } from './countdownSound'

type CountdownCallback = (remainingSeconds: number) => void

export class Countdown {
  private initialSeconds: number
  private remainingSeconds: number
  private timerId: NodeJS.Timeout | null
  private onTick: CountdownCallback | null
  onComplete: (() => void) | null
  isRunning: boolean

  constructor(
    initialSeconds: number,
    onTick: CountdownCallback | null,
    onComplete: (() => void) | null
  ) {
    this.initialSeconds = initialSeconds
    this.onTick = onTick
    this.onComplete = onComplete
    this.remainingSeconds = initialSeconds
    this.timerId = null
    this.isRunning = false
  }

  start(): void {
    // First ensure any existing timer is cleared
    this.stop()

    this.remainingSeconds = this.initialSeconds
    this.isRunning = true

    if (this.remainingSeconds <= 0) {
      console.warn(
        `[Countdown] start() called with initialSeconds=${this.initialSeconds} — firing onComplete immediately`
      )
      if (this.onComplete) this.onComplete()
      return
    }

    this.timerId = setInterval(() => {
      if (!this.isRunning) {
        this.stop()
        return
      }

      this.remainingSeconds -= 1

      if (this.remainingSeconds <= 0) {
        this.stop()
        if (this.onComplete) {
          this.onComplete()
        }
      } else {
        if (this.onTick) this.onTick(this.remainingSeconds)
      }
    }, 1000)
  }

  stop(): void {
    this.isRunning = false
    if (this.timerId) {
      clearInterval(this.timerId)
      this.timerId = null
    }
  }

  getRemainingSeconds(): number {
    return this.remainingSeconds
  }

  setRemainingSeconds(seconds: number): void {
    this.initialSeconds = seconds
    // Also update remainingSeconds if countdown is not running
    // This ensures the countdown starts with the correct value
    if (!this.isRunning) {
      this.remainingSeconds = seconds
    }
  }

  getFormattedRemainingSeconds(): string {
    const minutes = Math.floor(this.remainingSeconds / 60)
    const remainingSeconds = this.remainingSeconds % 60
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`
  }
}

/** Shown before the first session countdown tick; matches MM:SS width (view pages). */
export const COUNTDOWN_PLACEHOLDER = '--:--'

/** Seconds at/under which a session/step countdown is treated as "about to finish". */
export const COUNTDOWN_URGENT_SECONDS = 10

/**
 * True when a formatted "MM:SS" (or "M:SS") countdown string is at/under the
 * urgency threshold — used to turn the plain timer text red as it runs out.
 * Returns false for the "--:--" placeholder and anything unparseable.
 */
export const isCountdownUrgent = (
  formatted: string,
  thresholdSeconds: number = COUNTDOWN_URGENT_SECONDS
): boolean => {
  const m = /^(\d+):(\d{2})$/.exec(formatted.trim())
  if (!m) return false
  const total = parseInt(m[1], 10) * 60 + parseInt(m[2], 10)
  return total <= thresholdSeconds
}

/**
 * A React hook to create and manage a countdown with callbacks that always use the latest state
 */
export const useCountdown = (
  initialSeconds: number,
  options?: {
    /**
     * Mark this as a CAPTURE countdown, so it can be given an audible click on every
     * second the guest sees (project setting + `countdown_sound` flag; silent unless
     * both are on). Opt-in per call site because this hook also drives payment, idle
     * and session timers, and none of those should make noise.
     */
    audioCue?: boolean
  }
): {
  start: () => void
  stop: () => void
  getRemainingSeconds: () => number
  getFormattedRemainingSeconds: () => string
  setRemainingSeconds: (seconds: number) => void
  setOnTick: (callback: CountdownCallback | null) => void
  setOnComplete: (callback: (() => void) | null) => void
} => {
  // Keep reference to the countdown instance
  const countdownRef = useRef<Countdown | null>(null)

  // Store the callbacks as refs to prevent recreation on each render
  const onTickRef = useRef<CountdownCallback | null>(null)
  const onCompleteRef = useRef<(() => void) | null>(null)
  // Held in a ref so toggling it can never rebuild the Countdown instance mid-session.
  const audioCueRef = useRef<boolean>(options?.audioCue ?? false)
  audioCueRef.current = options?.audioCue ?? false

  // Create wrapper functions that will call the latest callback
  const onTickWrapper = useCallback((remainingSeconds: number) => {
    // Rides the visible tick rather than a pre-scheduled timeline: the click has to
    // match the number the guest is looking at, not an idealised clock.
    if (audioCueRef.current) playCountdownTick()
    if (onTickRef.current) {
      onTickRef.current(remainingSeconds)
    }
  }, [])

  const onCompleteWrapper = useCallback(() => {
    if (onCompleteRef.current) {
      onCompleteRef.current()
    }
  }, [])

  // Initialize countdown on mount
  useEffect(() => {
    countdownRef.current = new Countdown(initialSeconds, onTickWrapper, onCompleteWrapper)

    return (): void => {
      // Clean up on unmount
      if (countdownRef.current) {
        countdownRef.current.stop()
      }
    }
  }, [initialSeconds, onTickWrapper, onCompleteWrapper])

  // Return methods to control countdown and set callbacks
  return {
    start: useCallback(() => {
      const countdown = countdownRef.current
      if (countdown) {
        // The first number is rendered by start() itself and never reaches onTick, so
        // without this a 3-2-1 countdown would only click twice.
        if (audioCueRef.current) playCountdownTick()
        countdown.start()
      }
    }, []),

    stop: useCallback(() => {
      const countdown = countdownRef.current
      if (countdown) {
        countdown.stop()
      }
    }, []),

    getRemainingSeconds: useCallback(() => {
      return countdownRef.current?.getRemainingSeconds() || 0
    }, []),

    getFormattedRemainingSeconds: useCallback(() => {
      return countdownRef.current?.getFormattedRemainingSeconds() || '00:00'
    }, []),

    setRemainingSeconds: useCallback((seconds: number) => {
      const countdown = countdownRef.current
      if (countdown) {
        countdown.setRemainingSeconds(seconds)
      }
    }, []),

    setOnTick: useCallback((callback: CountdownCallback | null) => {
      onTickRef.current = callback
    }, []),

    setOnComplete: useCallback((callback: (() => void) | null) => {
      onCompleteRef.current = callback
    }, [])
  }
}
